import Api from '../../../../shared/Api';
import { Promise } from 'es6-promise';
import {
    Recipients,
    ICreateEmailCommand,
    ICreateSmsCommand,
    IEditOrderRowViewModel
} from './Types';
import * as OrderState from './OrderState';

class BasicOrderInfoApi {
    static createOrderRow(orderId: number, data: any): Promise<any> {
        return new Api().post(`/views/orders/${orderId}/rows`, data);
    }

    static view(orderId: number): Promise<any> {
        return new Api().get(`/views/orders/${orderId}/basic`);
    }

    static editOrderRow(orderId: number, rowId: number): Promise<IEditOrderRowViewModel> {
        return new Api().get(`/views/orders/${orderId}/rows/${rowId}/edit`);
    }

    static updateOrderRow(orderId: number, data: any): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/rows/${data.id}`, data);
    }

    static deleteOrderRow(orderId: number, rowId: number): Promise<any> {
        return new Api().delete(`/views/orders/${orderId}/rows/${rowId}`);
    }

    static getOrderRowDeleteStatus(
        orderId: number,
        rowId: number
    ): Promise<{ operationWillCauseFilesToBeRemoved: boolean }> {
        return new Api().get(`/views/orders/${orderId}/rows/${rowId}/status`, {
            type: 'delete'
        });
    }

    static getOrderRowEditStatus(
        orderId: number,
        rowId: number,
        newProductId: number
    ): Promise<{ operationWillCauseFilesToBeRemoved: boolean }> {
        return new Api().get(`/views/orders/${orderId}/rows/${rowId}/status`, {
            type: 'edit',
            newProductId
        });
    }

    static getEmails(orderId: number): Promise<any> {
        return new Api().get(`/views/orders/${orderId}/emails`);
    }

    static newEmail(orderId: number, recipient: Recipients): Promise<any> {
        return new Api().get(`/views/orders/${orderId}/emails/new/${recipient}`);
    }

    static createEmail(orderId: number, data: ICreateEmailCommand): Promise<any> {
        return new Api().post(`/views/orders/${orderId}/emails/create`, data);
    }

    static getSms(orderId: number): Promise<any> {
        return new Api().get(`/views/orders/${orderId}/sms`);
    }

    static newSms(orderId: number, recipient: Recipients): Promise<any> {
        return new Api().get(`/views/orders/${orderId}/sms/new/${recipient}`);
    }

    static createSms(orderId: number, data: ICreateSmsCommand): Promise<any> {
        return new Api().post(`/views/orders/${orderId}/sms/create`, data);
    }

    static preCheckUnbooking(calendarItemId: number): Promise<any> {
        return new Api().get(`/views/photographer-booking/${calendarItemId}/precheck`);
    }

    static unbookPhotographer(calendarItemId: number): Promise<any> {
        return new Api().delete(`/views/photographer-booking/${calendarItemId}`);
    }

    static unbookStylingUser(calendarItemId: number): Promise<any> {
        return new Api().delete(`/views/stylinguser-booking/${calendarItemId}`);
    }

    static markBookingRequestAsHandled(orderId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/booking-request/mark-as-handled`);
    }

    static markAsUnread(orderId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/markasunread`);
    }

    static unarchivePhoto(orderId: number, site: number): Promise<any> {
        return new Api().post(`/views/orders/${orderId}/unarchive/photo`, {
            site
        });
    }

    static unarchivePlan(orderId: number, site: number): Promise<any> {
        return new Api().post(`/views/orders/${orderId}/unarchive/plan`, {
            site
        });
    }

    static updatePreventEmailsToBroker(orderId: number, preventEmails: boolean): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/prevent-email-broker`, {
            value: preventEmails
        });
    }

    static updatePreventEmailsToPhotographer(
        orderId: number,
        preventEmails: boolean
    ): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/prevent-email-photographer`, {
            value: preventEmails
        });
    }

    static updatePreventCommunicationToPropertyOwner(
        orderId: number,
        preventEmails: boolean
    ): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/prevent-communication-property-owner`, {
            value: preventEmails
        });
    }

    static updateOwnerCommunicationLanguage(orderId: number, language: string): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/owner-language`, {
            language: language
        });
    }

    static UpdateIsDirectDelivery(orderId: number, isDirectDelivery: boolean): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/is-direct-delivery`, {
            isDirectDelivery: isDirectDelivery
        });
    }

    static updateImageEditorFirm(orderId: number, imageEditorFirmId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/image-editor-firm`, {
            imageEditorFirmId: imageEditorFirmId
        });
    }

    static updateImageEditor(orderId: number, imageEditorId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/image-editor`, {
            imageEditorId: imageEditorId
        });
    }

    static updateExtraDeliveryRecipients(
        orderId: number,
        extraDeliveryRecipients: string
    ): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/extra-delivery-recipients`, {
            extraDeliveryRecipients: extraDeliveryRecipients
        });
    }

    static setOrderToUploader(orderId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/set-order-to-uploader`, {});
    }

    static canRemovePlanOrder(orderId: number): Promise<any> {
        return new Api().get(`/views/orders/${orderId}/orderstate/validate-plan`);
    }

    static updateOrderState(orderId: number, orderState: number): Promise<any> {
        let url = `/views/orders/${orderId}/orderstate/`;

        switch (orderState) {
            case OrderState.Normal:
                url += 'normal';
                break;
            case OrderState.Removed:
                url += 'remove';
                break;
            case OrderState.BlockedForBooking:
                url += 'blocked-for-booking';
                break;
            case OrderState.BlockedForInvoice:
                url += 'blocked-for-invoice';
                break;
        }

        return new Api().put(url, {});
    }

    static lockPhotographer(orderId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/photographer-lock?lock=true`);
    }

    static unlockPhotographer(orderId: number): Promise<any> {
        return new Api().put(`/views/orders/${orderId}/photographer-lock?lock=false`);
    }

    static downloadFile(warmStorageReference: string): Promise<any> {
        return new Api().downloadFile(`/file/${warmStorageReference}`);
    }
}

export default BasicOrderInfoApi;
